import React, { useEffect, useState } from "react";
import { api, fmtBRL, CATEGORIA_LABEL, CATEGORIA_OPTS, formatError } from "@/lib/api";
import { SectionTitle, Badge, Btn, Modal, Field, SelectField, TextArea, EmptyState } from "@/components/Brutal";
import { ExportButtons, downloadLabels } from "@/components/ExportButtons";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, Tag } from "lucide-react";

const UNIDADES_MEDIDA = ["UN", "PC", "PCT", "CX", "Fardo", "Saco", "Litro", "Kg", "Metro", "Rolo", "Par", "Cuba"];
const empty = { codigo: "", codigo_barras: "", nome: "", categoria: "Outro", unidade: "UN", preco_custo: 0, preco_venda: 0, estoque_atual: 0, estoque_minimo: 0, localizacao: "", lote: "", fornecedor: "", fabricante: "", data_validade: "", validade_epi_dias: "", ca: "", ca_validade: "", descricao: "" };

function caStatusBadge(p) {
  const ca = p.ca || "";
  const val = p.ca_validade;
  if (!ca && !val) return <span className="text-[#aaa] text-xs">sem CA</span>;
  let diasRestantes = null;
  let variant = "success";
  let label = "VÁLIDO";
  if (val) {
    try {
      const dv = new Date(val);
      diasRestantes = Math.floor((dv - new Date()) / (1000 * 60 * 60 * 24));
      if (diasRestantes < 0) { variant = "danger"; label = "VENCIDO"; }
      else if (diasRestantes <= 30) { variant = "warn"; label = "A VENCER"; }
    } catch {}
  }
  return (
    <div className="flex items-center gap-2">
      <Badge variant={variant}>{label}</Badge>
      <div className="font-mono-data text-[10px] text-[#555]">
        {ca && <div className="font-bold text-[#111]">{ca}</div>}
        {val && <div>{new Date(val).toLocaleDateString("pt-BR")}{diasRestantes !== null && ` · ${diasRestantes >= 0 ? `${diasRestantes}d` : `vencido há ${Math.abs(diasRestantes)}d`}`}</div>}
      </div>
    </div>
  );
}

export default function Products() {
  const { hasRole } = useAuth();
  const canEdit = hasRole("almoxarife");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());

  const toggleSel = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const printLabels = async (ids) => {
    if (ids.length === 0) { toast.error("Selecione ao menos um produto"); return; }
    const copies = parseInt(prompt(`Quantas cópias de cada etiqueta?`, "1") || "1");
    if (!copies || copies < 1) return;
    try { await downloadLabels(ids, copies); toast.success("Etiquetas geradas"); }
    catch (e) { toast.error(e.message); }
  };

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/products");
    setItems(data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    // Preço de custo obrigatório e > 0
    const precoCusto = parseFloat(form.preco_custo);
    if (!precoCusto || precoCusto <= 0) {
      toast.error("Preço de custo é obrigatório e deve ser maior que zero");
      return;
    }
    try {
      const payload = { ...form,
        preco_custo: precoCusto,
        preco_venda: parseFloat(form.preco_venda) || 0,
        estoque_atual: parseFloat(form.estoque_atual) || 0,
        estoque_minimo: parseFloat(form.estoque_minimo) || 0,
        validade_epi_dias: form.validade_epi_dias ? parseInt(form.validade_epi_dias) : null,
        data_validade: form.data_validade || null,
      };
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Produto atualizado" : "Produto criado");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!confirm("Excluir produto?")) return;
    try { await api.delete(`/products/${id}`); toast.success("Excluído"); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const filtered = items.filter((p) => {
    if (cat && p.categoria !== cat) return false;
    const s = q.toLowerCase();
    return !s || p.codigo.toLowerCase().includes(s) || p.nome.toLowerCase().includes(s) || (p.codigo_barras || "").toLowerCase().includes(s);
  });

  return (
    <div data-testid="products-page">
      <SectionTitle action={<div className="flex items-center gap-2">
        <ExportButtons report="products" />
        {canEdit && selected.size > 0 && (
          <button onClick={() => printLabels(Array.from(selected))} className="brutal-btn bg-[#3D6B57] text-white px-3 py-1.5 font-display font-bold uppercase text-[10px] tracking-widest inline-flex items-center gap-1.5" data-testid="print-labels-btn">
            <Tag className="w-3.5 h-3.5" /> Etiquetas ({selected.size})
          </button>
        )}
        {canEdit && (
          <Btn onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} data-testid="add-product-btn"><Plus className="w-4 h-4" />Novo Produto</Btn>
        )}
      </div>}>Catálogo de Produtos</SectionTitle>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="brutal-card-sm flex items-center gap-2 px-3 py-2 bg-white max-w-md flex-1">
          <Search className="w-4 h-4 text-[#555]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por código, nome ou EAN..." className="flex-1 outline-none text-sm bg-transparent" data-testid="products-search" />
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)} className="border-2 border-[#111] p-2 text-sm bg-white">
          <option value="">Todas categorias</option>
          {CATEGORIA_OPTS.map((c) => <option key={c} value={c}>{CATEGORIA_LABEL[c]}</option>)}
        </select>
      </div>

      <div className="brutal-card overflow-x-auto">
        <table className="brutal-table">
          <thead><tr><th className="w-8"></th><th>Código</th><th>Nome</th><th>Categoria</th><th>Unidade</th><th className="text-right">Estoque</th><th className="text-right">Mín</th><th>CA / Validade</th><th>Status</th>{canEdit && <th className="w-32">Ações</th>}</tr></thead>
          <tbody>
            {loading && (<tr><td colSpan={10} className="text-center py-8 font-mono-data text-sm text-[#777]">Carregando...</td></tr>)}
            {!loading && filtered.length === 0 && <EmptyState message="Nenhum produto encontrado." cols={canEdit ? 10 : 9} />}
            {filtered.map((p) => {
              const low = p.estoque_minimo > 0 && p.estoque_atual <= p.estoque_minimo;
              const filialLabel = p.filial === "km117" ? "KM 117" : (p.filial === "cupari" ? "Cuparí" : (p.filial || "—"));
              const caInfo = p.categoria === "EPI" ? caStatusBadge(p) : null;
              return (
                <tr key={p.id} data-testid={`product-row-${p.codigo}`}>
                  <td><input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleSel(p.id)} data-testid={`sel-${p.codigo}`} /></td>
                  <td className="font-mono-data font-bold">{p.codigo}</td>
                  <td>{p.nome}</td>
                  <td><Badge variant="green">{CATEGORIA_LABEL[p.categoria]}</Badge></td>
                  <td><Badge variant={p.filial === "cupari" ? "orange" : "default"}>{filialLabel}</Badge></td>
                  <td className="text-right font-mono-data font-bold">{p.estoque_atual} {p.unidade}</td>
                  <td className="text-right font-mono-data text-[#555]">{p.estoque_minimo}</td>
                  <td>{caInfo || <span className="text-[#aaa] text-xs">—</span>}</td>
                  <td>{low ? <Badge variant="danger">Baixo</Badge> : <Badge variant="success">OK</Badge>}</td>
                  {canEdit && (
                    <td><div className="flex gap-1">
                      <button onClick={() => printLabels([p.id])} className="p-1.5 hover:bg-[#3D6B57] hover:text-white border border-[#111]" title="Imprimir etiqueta"><Tag className="w-3.5 h-3.5" /></button>
                      <button onClick={() => { setEditing(p); setForm({ ...p, validade_epi_dias: p.validade_epi_dias ?? "", data_validade: p.data_validade ? p.data_validade.split("T")[0] : "", ca: p.ca ?? "", ca_validade: p.ca_validade ?? "" }); setOpen(true); }} className="p-1.5 hover:bg-[#FF4F00] hover:text-white border border-[#111]"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => remove(p.id)} className="p-1.5 hover:bg-[#E60000] hover:text-white border border-[#111]"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div></td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar Produto" : "Novo Produto"} size="xl">
        <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="product-form">
          <Field label="Código *" required value={form.codigo} onChange={(v) => setForm({ ...form, codigo: v })} testid="p-codigo" />
          <Field label="Código de Barras (EAN)" value={form.codigo_barras} onChange={(v) => setForm({ ...form, codigo_barras: v })} testid="p-ean" />
          <Field label="Nome *" required value={form.nome} onChange={(v) => setForm({ ...form, nome: v })} testid="p-nome" />
          <SelectField label="Categoria *" required value={form.categoria} onChange={(v) => setForm({ ...form, categoria: v })} options={CATEGORIA_OPTS.map((c) => ({ value: c, label: CATEGORIA_LABEL[c] }))} testid="p-cat" />
          <SelectField label="Unidade" value={form.unidade} onChange={(v) => setForm({ ...form, unidade: v })} options={UNIDADES_MEDIDA.map((u) => ({ value: u, label: u }))} testid="p-unidade" />
          <Field label="Preço Custo *" required type="number" value={form.preco_custo} onChange={(v) => setForm({ ...form, preco_custo: v })} testid="p-preco-custo" placeholder="Obrigatório · Ex: 12.50" />
          <Field label="Estoque Atual" type="number" value={form.estoque_atual} onChange={(v) => setForm({ ...form, estoque_atual: v })} />
          <Field label="Estoque Mínimo" type="number" value={form.estoque_minimo} onChange={(v) => setForm({ ...form, estoque_minimo: v })} />
          <Field label="Localização" value={form.localizacao} onChange={(v) => setForm({ ...form, localizacao: v })} placeholder="Prateleira A2" />
          <Field label="Lote" value={form.lote} onChange={(v) => setForm({ ...form, lote: v })} />
          <Field label="Fornecedor" value={form.fornecedor} onChange={(v) => setForm({ ...form, fornecedor: v })} />
          <Field label="Validade do Produto" type="date" value={form.data_validade} onChange={(v) => setForm({ ...form, data_validade: v })} />
          {form.categoria === "EPI" && (
            <div className="col-span-2 brutal-card-sm p-3 bg-[#E8F0EA] border-2 border-[#3D6B57]">
              <div className="font-display font-bold uppercase text-xs tracking-wider mb-3 text-[#3D6B57]">🦺 Dados específicos do EPI</div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Nº do CA (Certificado de Aprovação) *" value={form.ca} onChange={(v) => setForm({ ...form, ca: v })} placeholder="Ex: CA 41585" testid="p-ca" />
                <Field label="Validade do CA *" type="date" value={form.ca_validade ? form.ca_validade.split("T")[0] : ""} onChange={(v) => setForm({ ...form, ca_validade: v })} testid="p-ca-val" />
                <Field label="Fabricante" value={form.fabricante} onChange={(v) => setForm({ ...form, fabricante: v })} placeholder="Ex: 3M, Bracol, Vonder…" testid="p-fab" />
                <Field label="Validade em uso (dias após entrega)" type="number" value={form.validade_epi_dias} onChange={(v) => setForm({ ...form, validade_epi_dias: v })} placeholder="180" />
              </div>
            </div>
          )}
          <TextArea label="Descrição" value={form.descricao} onChange={(v) => setForm({ ...form, descricao: v })} colspan />
          <div className="col-span-2 flex justify-end gap-2 pt-2 border-t-2 border-[#111]">
            <Btn type="button" variant="secondary" onClick={() => setOpen(false)}>Cancelar</Btn>
            <Btn type="submit" data-testid="save-product-btn">{editing ? "Salvar" : "Criar"}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
